/**
 * Futures-Spot Table Component
 *
 * Displays real-time price data for futures-spot arbitrage opportunities.
 * Features:
 * - Side-by-side spot and futures prices
 * - Price differential calculation (absolute and percentage)
 * - Sorting by symbol, price, differential
 * - Filtering by symbol, exchange, differential threshold
 * - Mobile-responsive layout
 * - Cyberpunk theme styling
 */

import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Skeleton,
  Box,
  Typography,
  TextField,
  Chip,
  useTheme,
  useMediaQuery,
  Stack,
  InputAdornment,
  IconButton,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import ArrowDownward from '@mui/icons-material/ArrowDownward';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { useFuturesSpotData } from '../../contexts/FuturesSpotDataContext';
import { FundingCountdown } from './FundingCountdown';
import { OrderbookDepthModal } from './OrderbookDepthModal';
import { getExchangeLogo, getExchangeSpotUrl, EXCHANGE_NAMES } from '../../utils/exchangeUrls';
import { Avatar } from '@mui/material';
import { usePriceUpdates } from '../../hooks/usePriceUpdates';

/**
 * Sort direction type
 */
type SortDirection = 'asc' | 'desc';

/**
 * Sort field type
 */
type SortField = 'symbol' | 'spotPrice' | 'futuresPrice' | 'fundingRate';

/**
 * Component props
 */
interface FuturesSpotTableProps {
  /** Enable filtering UI (default: true) */
  enableFiltering?: boolean;

  /** Minimum differential filter (default: null, no filter) */
  minDifferential?: number | null;

  /** Maximum items to display (default: 100) */
  maxItems?: number;
}

/**
 * Number of skeleton rows for loading state
 */
const SKELETON_ROWS = 10;

/**
 * Futures-Spot Table Component
 */
export const FuturesSpotTable: React.FC<FuturesSpotTableProps> = ({
  enableFiltering = true,
  minDifferential = null,
  maxItems = 100,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  // Get data from context
  const { state, getPriceList } = useFuturesSpotData();
  const { loading, prices } = state;

  // Get exchange rates for currency conversion
  const exchangeRates = usePriceUpdates();

  // Filter state
  const [symbolFilter, setSymbolFilter] = useState('');
  const [differentialFilter, setDifferentialFilter] = useState<number | null>(minDifferential);

  // Sort state
  const [sortField, setSortField] = useState<SortField>('fundingRate');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<{
    symbol: string;
    spotExchange: string;
    futuresExchange: string;
  } | null>(null);

  /**
   * Handle sort field change
   */
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to descending
      setSortField(field);
      setSortDirection('desc');
    }
  };

  /**
   * Handle opening orderbook depth modal
   */
  const handleOpenModal = (row: typeof selectedRow) => {
    setSelectedRow(row);
    setModalOpen(true);
  };

  /**
   * Handle closing orderbook depth modal
   */
  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedRow(null);
  };

  /**
   * Group data by base asset and aggregate spot exchanges
   */
  interface GroupedData {
    baseAsset: string;
    spotPrices: Array<{
      exchange: string; // Exchange name string (e.g., "Binance", "Upbit")
      price: number;
      symbol: string;
      quoteAsset: string;
    }>;
    futuresPrice: number | null;
    percentageDifferential: number | null;
    absoluteDifferential: number | null;
    fundingRate: number | null;
    nextFundingTime: number | null;
    quoteAsset: string; // Primary quote asset (USDT)
  }

  /**
   * Filter and sort price data, grouped by base asset
   */
  const filteredAndSortedData = useMemo(() => {
    const priceList = getPriceList();

    // Helper function to convert spot price to USDT
    const convertPriceToUSDT = (price: number, quoteAsset: string): number | null => {
      if (quoteAsset === 'USDT') return price;

      if (quoteAsset === 'KRW' && exchangeRates?.upbitBtcKrw && exchangeRates?.binanceBtcUsdt) {
        // KRW to USDT: price / (upbitBtcKrw / binanceBtcUsdt)
        const exchangeRate = exchangeRates.upbitBtcKrw / exchangeRates.binanceBtcUsdt;
        return price / exchangeRate;
      }

      return null; // Cannot convert
    };

    // Group by base asset
    const grouped = new Map<string, GroupedData>();

    priceList.forEach((item) => {
      if (!item.spotPrice || !item.futuresPrice) return;

      const key = item.baseAsset;

      if (!grouped.has(key)) {
        grouped.set(key, {
          baseAsset: item.baseAsset,
          spotPrices: [],
          futuresPrice: item.futuresPrice,
          percentageDifferential: item.percentageDifferential,
          absoluteDifferential: item.absoluteDifferential,
          fundingRate: item.fundingRate,
          nextFundingTime: item.nextFundingTime,
          quoteAsset: item.quoteAsset,
        });
      }

      const group = grouped.get(key)!;
      group.spotPrices.push({
        exchange: item.spotExchange,
        price: item.spotPrice,
        symbol: item.symbol,
        quoteAsset: item.quoteAsset,
      });
    });

    // Convert to array and filter for 2+ exchanges
    let result = Array.from(grouped.values()).filter((item) => item.spotPrices.length >= 2);

    // Apply base asset filter
    if (symbolFilter) {
      result = result.filter((item) =>
        item.baseAsset.toLowerCase().includes(symbolFilter.toLowerCase())
      );
    }

    // Apply differential filter
    if (differentialFilter != null) {
      result = result.filter(
        (item) =>
          item.percentageDifferential != null &&
          Math.abs(item.percentageDifferential) >= differentialFilter
      );
    }

    // Filter by funding rate: only show >0 when sorting by fundingRate descending (default)
    // BUT: skip this filter if user is actively searching (symbolFilter is set)
    if (sortField === 'fundingRate' && sortDirection === 'desc' && !symbolFilter) {
      result = result.filter((item) => (item.fundingRate ?? 0) > 0);
    }

    // Helper function to calculate highest % diff for an item
    const getHighestPercentDiff = (item: GroupedData): number | null => {
      if (!item.futuresPrice || item.spotPrices.length === 0) return null;

      const diffs = item.spotPrices.map(sp => {
        // Convert spot price to USDT for accurate comparison
        const spotPriceUSDT = convertPriceToUSDT(sp.price, sp.quoteAsset);
        if (spotPriceUSDT === null) return -Infinity;

        // Calculate percentage difference: (spot - futures) / spot * 100
        const diff = ((spotPriceUSDT - item.futuresPrice!) / spotPriceUSDT) * 100;
        return diff;
      });

      // Most negative = best opportunity for entry
      return Math.min(...diffs);
    };

    // Sort with primary and secondary keys
    result.sort((a, b) => {
      // Primary sort based on selected field
      let primaryA: number | string | null;
      let primaryB: number | string | null;

      switch (sortField) {
        case 'symbol':
          primaryA = a.baseAsset;
          primaryB = b.baseAsset;
          break;
        case 'spotPrice':
          // Use highest % diff among all spot prices
          primaryA = getHighestPercentDiff(a);
          primaryB = getHighestPercentDiff(b);
          break;
        case 'futuresPrice':
          primaryA = a.futuresPrice;
          primaryB = b.futuresPrice;
          break;
        case 'fundingRate':
          primaryA = a.fundingRate;
          primaryB = b.fundingRate;
          break;
        default:
          return 0;
      }

      // Handle null values for primary sort
      if (primaryA === null && primaryB === null) {
        // If both null, sort by secondary (spot price % diff descending)
        const secondaryA = getHighestPercentDiff(a) ?? -Infinity;
        const secondaryB = getHighestPercentDiff(b) ?? -Infinity;
        return secondaryB - secondaryA;
      }
      if (primaryA === null) return 1;
      if (primaryB === null) return -1;

      // Primary sort comparison
      let primaryCompare = 0;
      if (typeof primaryA === 'string' && typeof primaryB === 'string') {
        primaryCompare = sortDirection === 'asc'
          ? primaryA.localeCompare(primaryB)
          : primaryB.localeCompare(primaryA);
      } else {
        const aNum = Number(primaryA);
        const bNum = Number(primaryB);
        primaryCompare = sortDirection === 'asc' ? aNum - bNum : bNum - aNum;
      }

      // If primary values are equal (especially for fundingRate), use secondary sort
      if (primaryCompare === 0) {
        // Secondary sort: spot price % diff descending
        const secondaryA = getHighestPercentDiff(a) ?? -Infinity;
        const secondaryB = getHighestPercentDiff(b) ?? -Infinity;
        return secondaryB - secondaryA; // Always descending for secondary
      }

      return primaryCompare;
    });

    // Return all results (no limit)
    return result;
  }, [getPriceList, symbolFilter, differentialFilter, sortField, sortDirection, exchangeRates]);

  /**
   * Format price with appropriate decimal places
   */
  /**
   * Convert spot price to USDT using exchange rates
   */
  const convertToUSDT = (price: number, quoteAsset: string): number | null => {
    if (quoteAsset === 'USDT') return price;

    if (quoteAsset === 'KRW' && exchangeRates?.upbitBtcKrw && exchangeRates?.binanceBtcUsdt) {
      // KRW to USDT: price / (upbitBtcKrw / binanceBtcUsdt)
      const exchangeRate = exchangeRates.upbitBtcKrw / exchangeRates.binanceBtcUsdt;
      return price / exchangeRate;
    }

    return null; // Cannot convert
  };

  const formatPrice = (price: number | null, quoteAsset: string): string => {
    if (price === null) return '—';

    // Get currency symbol based on quote asset
    const getCurrencySymbol = (asset: string): string => {
      switch (asset.toUpperCase()) {
        case 'KRW': return '₩';
        case 'USDT':
        case 'USD': return '$';
        case 'BTC': return '₿';
        case 'ETH': return 'Ξ';
        default: return asset + ' ';
      }
    };

    // Determine decimals based on currency
    const decimals = quoteAsset.toUpperCase() === 'KRW'
      ? 0  // KRW typically has no decimals
      : 4; // All other currencies use 4 decimal points

    const symbol = getCurrencySymbol(quoteAsset);
    const formattedPrice = price.toFixed(decimals);

    // For KRW, add thousand separators
    if (quoteAsset.toUpperCase() === 'KRW') {
      const withSeparators = parseInt(formattedPrice).toLocaleString('en-US');
      return `${symbol}${withSeparators}`;
    }

    return `${symbol}${formattedPrice}`;
  };

  /**
   * Get exchange index from Exchange string
   */
  const getExchangeIndexFromString = (exchangeString: string): number => {
    const lowerExchange = exchangeString.toLowerCase();
    switch (lowerExchange) {
      case 'upbit': return 0;
      case 'bithumb': return 1;
      case 'coinone': return 2;
      case 'binance': return 3;
      case 'htx': return 4;
      case 'gateio': case 'gate.io': return 5;
      case 'kucoin': return 6;
      case 'okx': return 7;
      case 'mexc': return 8;
      case 'bybit': return 9;
      case 'bitget': return 10;
      case 'coinbase': return 11;
      default: return 3; // Default to Binance
    }
  };

  /**
   * Get funding rate color based on significance
   * > 0.1% = green, < -0.1% = orange/red, else default
   */
  const getFundingRateColor = (fundingRate: number | null): string => {
    if (fundingRate === null) return 'inherit';
    if (fundingRate > 0.001) return '#4caf50';  // >0.1% = green
    if (fundingRate < -0.001) return '#ff6b35'; // <-0.1% = bright orange-red (more visible)
    return 'inherit';
  };

  /**
   * Get funding rate icon based on significance
   */
  const getFundingRateIcon = (fundingRate: number | null): JSX.Element | null => {
    if (fundingRate === null) return null;
    if (fundingRate > 0.001) return <ArrowUpward fontSize="small" sx={{ color: '#4caf50' }} />;
    if (fundingRate < -0.001) return <ArrowDownward fontSize="small" sx={{ color: '#ff6b35' }} />;
    return null;
  };

  /**
   * Format funding rate as percentage
   */
  const formatFundingRate = (fundingRate: number | null): string => {
    if (fundingRate === null) return '—';
    return `${(fundingRate * 100).toFixed(4)}%`;
  };

  /**
   * Loading skeleton
   */
  if (loading) {
    return (
      <TableContainer component={Paper} sx={{ background: theme.palette.background.paper }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Symbol</TableCell>
              <TableCell>Futures Price</TableCell>
              <TableCell>Spot Price</TableCell>
              <TableCell>Funding Rate</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.from({ length: SKELETON_ROWS }).map((_, index) => (
              <TableRow key={index}>
                <TableCell>
                  <Skeleton variant="text" width={100} />
                </TableCell>
                <TableCell>
                  <Skeleton variant="text" width={120} />
                </TableCell>
                <TableCell>
                  <Skeleton variant="text" width={150} />
                </TableCell>
                <TableCell>
                  <Skeleton variant="text" width={130} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  /**
   * Empty state
   */
  if (prices.size === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 400,
          gap: 2,
          background: theme.palette.background.paper,
          borderRadius: 1,
          p: 4,
        }}
      >
        <TrendingUpIcon sx={{ fontSize: 64, color: theme.palette.text.disabled }} />
        <Typography variant="h6" color="text.secondary">
          No price data available
        </Typography>
        <Typography variant="body2" color="text.disabled">
          Waiting for exchange connections...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Filter Controls */}
      {enableFiltering && (
        <Stack
          direction={isMobile ? 'column' : 'row'}
          spacing={2}
          sx={{ mb: 2 }}
        >
          <TextField
            placeholder="Search symbol..."
            value={symbolFilter}
            onChange={(e) => setSymbolFilter(e.target.value)}
            size="small"
            fullWidth={isMobile}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{
              maxWidth: isMobile ? '100%' : 300,
            }}
          />
          <TextField
            label="Min Differential %"
            type="number"
            value={differentialFilter ?? ''}
            onChange={(e) =>
              setDifferentialFilter(e.target.value ? Number(e.target.value) : null)
            }
            size="small"
            fullWidth={isMobile}
            sx={{
              maxWidth: isMobile ? '100%' : 200,
            }}
          />
        </Stack>
      )}

      {/* Table */}
      <TableContainer
        component={Paper}
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`,
          borderRadius: 2,
          boxShadow: `0 0 20px ${theme.palette.primary.main}40`,
          overflowX: 'auto', // Enable horizontal scroll for mobile
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {/* Symbol */}
              <TableCell>
                <TableSortLabel
                  active={sortField === 'symbol'}
                  direction={sortField === 'symbol' ? sortDirection : 'asc'}
                  onClick={() => handleSort('symbol')}
                >
                  Symbol
                </TableSortLabel>
              </TableCell>

              {/* Futures Price - FIRST */}
              <TableCell align="right">
                <TableSortLabel
                  active={sortField === 'futuresPrice'}
                  direction={sortField === 'futuresPrice' ? sortDirection : 'asc'}
                  onClick={() => handleSort('futuresPrice')}
                >
                  Futures Price
                </TableSortLabel>
              </TableCell>

              {/* Spot Price - SECOND */}
              <TableCell align="left">
                <TableSortLabel
                  active={sortField === 'spotPrice'}
                  direction={sortField === 'spotPrice' ? sortDirection : 'asc'}
                  onClick={() => handleSort('spotPrice')}
                >
                  Spot Price (% Diff)
                </TableSortLabel>
              </TableCell>

              {/* Funding Rate - Sortable */}
              <TableCell align="right">
                <TableSortLabel
                  active={sortField === 'fundingRate'}
                  direction={sortField === 'fundingRate' ? sortDirection : 'asc'}
                  onClick={() => handleSort('fundingRate')}
                >
                  Funding Rate
                </TableSortLabel>
              </TableCell>

            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAndSortedData.map((item) => {
              return (
                <TableRow
                  key={item.baseAsset}
                  hover
                  sx={{
                    '&:hover': {
                      background: `${theme.palette.primary.main}10`,
                    },
                  }}
                >
                  {/* Symbol - Just base asset, clickable */}
                  <TableCell>
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      onClick={() => window.open(`/futures/${item.baseAsset}`, '_blank', 'noopener,noreferrer')}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          color: theme.palette.primary.light,
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      {item.baseAsset}
                    </Typography>
                  </TableCell>

                  {/* Futures Price - FIRST, simple display */}
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="bold">
                      {formatPrice(item.futuresPrice, 'USDT')}
                    </Typography>
                  </TableCell>

                  {/* Spot Price - SECOND, with percentage difference */}
                  <TableCell align="left">
                    <Stack spacing={0.5}>
                      {item.spotPrices.map((spotPrice) => {
                        const exchangeIndex = getExchangeIndexFromString(spotPrice.exchange);

                        // Convert spot price to USDT
                        const spotPriceUSDT = convertToUSDT(spotPrice.price, spotPrice.quoteAsset);

                        // Calculate percentage difference: (spot - futures) / spot * 100
                        let percentDiff: number | null = null;
                        if (spotPriceUSDT !== null && item.futuresPrice !== null) {
                          percentDiff = ((spotPriceUSDT - item.futuresPrice) / spotPriceUSDT) * 100;
                        }

                        const diffColor = percentDiff !== null && percentDiff < 0
                          ? theme.palette.success.main
                          : '#ff6b9d';

                        // Get exchange URL
                        const [base, quote] = spotPrice.symbol.split('/');
                        const exchangeUrl = getExchangeSpotUrl(exchangeIndex, base, quote);

                        const handlePriceClick = () => {
                          if (exchangeUrl) {
                            window.open(exchangeUrl, '_blank', 'noopener,noreferrer');
                          }
                        };

                        return (
                          <Box
                            key={`${spotPrice.exchange}-${spotPrice.symbol}`}
                            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                          >
                            {/* Action button - LEFT of the logo */}
                            <Tooltip title="View Orderbook Depth" arrow>
                              <IconButton
                                onClick={() => handleOpenModal({
                                  symbol: spotPrice.symbol,
                                  spotExchange: spotPrice.exchange,
                                  futuresExchange: 'Binance Futures',
                                })}
                                sx={{
                                  color: theme.palette.primary.main,
                                  '&:hover': {
                                    backgroundColor: `${theme.palette.primary.main}20`,
                                    color: theme.palette.primary.light,
                                  },
                                  width: 28,
                                  height: 28,
                                  minWidth: 28,
                                  minHeight: 28,
                                  padding: 0.5,
                                }}
                                size="small"
                              >
                                <RocketLaunchIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>

                            {/* Exchange logo (no badge, just logo) */}
                            <Avatar
                              src={getExchangeLogo(exchangeIndex)}
                              sx={{
                                width: 16,
                                height: 16,
                                flexShrink: 0,
                              }}
                            >
                              {spotPrice.exchange.substring(0, 1)}
                            </Avatar>

                            {/* Price in USDT - fixed width to prevent shifting, clickable */}
                            <Typography
                              variant="body2"
                              onClick={handlePriceClick}
                              sx={{
                                minWidth: '80px',
                                cursor: exchangeUrl ? 'pointer' : 'default',
                                '&:hover': exchangeUrl ? {
                                  textDecoration: 'underline',
                                  color: theme.palette.primary.light,
                                } : {},
                              }}
                            >
                              {spotPriceUSDT !== null ? formatPrice(spotPriceUSDT, 'USDT') : '—'}
                            </Typography>
                            {/* Percentage difference - positioned after price with padding */}
                            {percentDiff !== null && (
                              <Typography
                                variant="body2"
                                sx={{
                                  color: diffColor,
                                  fontWeight: 'bold',
                                  minWidth: '60px',
                                  pl: 1,
                                  fontSize: '0.875rem',
                                }}
                              >
                                ({percentDiff >= 0 ? '+' : ''}{percentDiff.toFixed(2)}%)
                              </Typography>
                            )}

                            {/* Original price for non-USDT pairs */}
                            {spotPrice.quoteAsset !== 'USDT' && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'text.secondary',
                                  fontSize: '0.75rem',
                                  pl: 0.5,
                                }}
                              >
                                [{formatPrice(spotPrice.price, spotPrice.quoteAsset)}]
                              </Typography>
                            )}
                          </Box>
                        );
                      })}
                    </Stack>
                  </TableCell>

                  {/* Funding Rate */}
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.25 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {getFundingRateIcon(item.fundingRate)}
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          sx={{ color: getFundingRateColor(item.fundingRate) }}
                        >
                          {formatFundingRate(item.fundingRate)}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                        <FundingCountdown nextFundingTime={item.nextFundingTime} />
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Results count */}
      <Box sx={{ mt: 2, textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          Showing {filteredAndSortedData.length} of {getPriceList().length} pairs
        </Typography>
      </Box>

      {/* Orderbook Depth Modal */}
      {selectedRow && (
        <OrderbookDepthModal
          open={modalOpen}
          onClose={handleCloseModal}
          symbol={selectedRow.symbol}
          spotExchange={selectedRow.spotExchange}
          futuresExchange={selectedRow.futuresExchange}
        />
      )}
    </Box>
  );
};
